// Commands Index - 指令索引文件
import { BaseCommand } from '../command-parser/BaseCommand.js';
import { xpToLevel } from '../realms.js';
import * as Skills from '../skills.js';
import * as Pills from '../pills.js';
import * as Treasures from '../treasures.js';
import * as Sects from '../sects.js';
import { UtilsManager } from '../utils/index.js';
import { COMMAND_MAPPINGS, getChineseCommand, getCommandDescription } from '../config/commands.js';

// 帮助指令
class HelpCommand extends BaseCommand {
  constructor(commandManager) {
    super('help', '帮助', '显示可用指令列表', false, commandManager);
  }

  execute(arg) {
    const state = this.commandManager.stateManager.getState();
    const availableCommands = this.commandManager.getAvailableCommands();

    // 过滤掉不应该在help中显示的指令
    const hiddenCommands = ['name', 'info', 'discoveries'];
    // 临时屏蔽的指令
    const tempHiddenCommands = ['learn', 'use', 'reset', 'cultivate', 'refine_pill', 'refine_treasure'];
    const allHidden = hiddenCommands.concat(tempHiddenCommands);
    const filteredCommands = availableCommands.filter(cmd => !allHidden.includes(cmd));

    // 使用配置中的中文命令名
    const chineseCommands = filteredCommands.map(cmd =>
      getChineseCommand(cmd)
    );

    const lines = ['可用指令: ' + chineseCommands.join(', ')];

    if (state.location === '洞府') {
      lines.push('在洞府中可以修炼炼丹炼器。');
    }

    lines.push('输入命令名查看更多信息，例如输入 宗门 查看可选门派');
    return lines;
  }
}

// 状态指令
class StatusCommand extends BaseCommand {
  constructor(commandManager) {
    super('status', '状态', '查看角色状态信息', false, commandManager);
  }

  execute(arg) {
    // 打开状态模态框
    this.commandManager.stateManager.updateState({ inStatusModal: true });
    return [{ type: 'open_status_modal' }];
  }
}

// 机缘指令
class ExploreCommand extends BaseCommand {
  constructor(commandManager) {
    super('explore', getChineseCommand('explore'), getCommandDescription('explore'), true, commandManager);
  }

  execute(arg) {
    // 如果有参数且是数字，执行机缘选择
    if (arg && !isNaN(parseInt(arg))) {
      return this.commandManager.explorationManager.handleExplorationChoice(arg);
    }
    // 否则打开探索模态框
    this.commandManager.stateManager.updateState({ inExplorationModal: true });
    return [{ type: 'open_exploration_modal' }];
  }
}

// 学习指令
class LearnCommand extends BaseCommand {
  constructor(commandManager) {
    super('learn', '学习', '学习技能', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    return Skills.learn(this.commandManager.stateManager.getState(), arg);
  }
}

// 使用物品指令
class UseCommand extends BaseCommand {
  constructor(commandManager) {
    super('use', '使用', '使用背包中的物品', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    if (!arg) {
      const bag = this.commandManager.stateManager.getBag();
      if (Object.keys(bag).length === 0) {
        return ['背包为空，无法使用物品。'];
      }
      const itemList = Object.keys(bag).map(it => {
        const count = bag[it];
        const countStr = count > 1 ? `×${count}` : '';
        return `${it}${countStr} - use ${it}`;
      });
      return ['可用物品：'].concat(itemList);
    }

    if (!this.commandManager.stateManager.hasItem(arg)) {
      return [`你的背包中没有 ${arg}。`];
    }

    // Check if it's a manual/recipe book for unlocking recipes
    if (arg.endsWith('秘籍') || arg.endsWith('丹方')) {
      return this.useManual(arg);
    }

    // Try pills first, then treasures
    const pills = Pills.listPills().map(p => p.toLowerCase());
    if (pills.indexOf(arg.toLowerCase()) !== -1) {
      const result = Pills.usePill(this.commandManager.stateManager.getState(), arg);
      if (result.success) {
        this.commandManager.stateManager.removeItemFromBag(arg);
      }
      return result.messages;
    } else {
      const result = Treasures.useTreasure(this.commandManager.stateManager.getState(), arg);
      if (result.success) {
        this.commandManager.stateManager.removeItemFromBag(arg);
      }
      return result.messages;
    }
  }

  // 使用秘籍或丹方解锁配方
  useManual(manualName) {
    const state = this.commandManager.stateManager.getState();

    // 从秘籍或丹方名称中提取原始配方名称（去掉"秘籍"或"丹方"后缀）
    let recipeName = manualName;
    if (manualName.endsWith('秘籍')) {
      recipeName = manualName.replace('秘籍', '');
    } else if (manualName.endsWith('丹方')) {
      recipeName = manualName.replace('丹方', '');
    }

    // 检查是否是有效的丹方
    const pillRecipes = Pills.listRefinablePills();
    if (!pillRecipes.includes(recipeName)) {
      return [`${manualName} 不是有效的秘籍或丹方。`];
    }

    // 检查是否已经解锁
    if (!state.unlockedPills) {
      state.unlockedPills = ['聚气丹', '回气丹']; // 初始化默认解锁
    }

    if (state.unlockedPills.includes(recipeName)) {
      return [`你已经掌握 ${recipeName} 的炼制方法了。`];
    }

    // 解锁配方
    state.unlockedPills.push(recipeName);

    // 消耗秘籍
    this.commandManager.stateManager.removeItemFromBag(manualName);

    // 保存状态
    this.commandManager.stateManager.saveState();

    return [`恭喜！你成功学习了 ${recipeName} 的炼制方法。现在可以在炼丹炉中使用它了。`];
  }
}

// 宗门指令
class JoinCommand extends BaseCommand {
  constructor(commandManager) {
    super('join', '宗门', '加入修仙门派', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    if (!arg) {
      // 打开宗门模态框
      this.commandManager.stateManager.updateState({ inSectModal: true });
      return [{ type: 'open_sect_modal' }];
    }
    const result = Sects.joinSect(this.commandManager.stateManager.getState(), arg);
    // 保存状态，因为joinSect直接修改了state
    this.commandManager.stateManager.saveState();
    return result;
  }
}

// 背包指令
class BagCommand extends BaseCommand {
  constructor(commandManager) {
    super('bag', '背包', '查看背包物品', false, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    // 打开背包模态框
    this.commandManager.stateManager.updateState({ inBagModal: true });
    return [{ type: 'open_bag_modal' }];
  }
}

// 地图指令
class TravelCommand extends BaseCommand {
  constructor(commandManager) {
    super('travel', '地图', '前往其他地点', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    // 如果有参数，直接旅行到指定地点（保持向后兼容）
    if (arg) {
      const places = ['洞府', '后山', '秘境', '云海', '禁地'];
      if (places.indexOf(arg) === -1) {
        return ['未知地点。可选：' + places.join('，')];
      }

      if (arg === '禁地') {
        // 进入禁地时退出炼丹炼器界面
        this.commandManager.stateManager.getState().inRefiningPill = false;
        this.commandManager.stateManager.getState().inRefiningTreasure = false;
        this.commandManager.stateManager.getState().refiningItemName = '';
        this.commandManager.forbiddenManager.generateMap();
        this.commandManager.stateManager.enterForbiddenMap();
        return ['你进入了禁地！地图已随机生成。', '可用命令：up, down, left, right, scan, exit, status, help'];
      } else {
        // 离开洞府时退出炼丹炼器界面
        if (this.commandManager.stateManager.getLocation() === '洞府' && arg !== '洞府') {
          this.commandManager.stateManager.getState().inRefiningPill = false;
          this.commandManager.stateManager.getState().inRefiningTreasure = false;
          this.commandManager.stateManager.getState().refiningItemName = '';
        }
        this.commandManager.stateManager.setLocation(arg);
        return [`你来到了 ${arg}。`];
      }
    }

    // 如果没有参数，打开旅行模态框
    return [{ type: 'open_travel_modal' }];
  }
}

// 重置指令
class ResetCommand extends BaseCommand {
  constructor(commandManager) {
    super('reset', '重置', '重置游戏进度', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    this.commandManager.stateManager.resetState();
    return ['已重置角色。'];
  }
}

// 修炼指令
class CultivateCommand extends BaseCommand {
  constructor(commandManager) {
    super('cultivate', '修炼', '修炼提升修为', true, commandManager);
    this.commandManager = commandManager;
  }

  isAvailable() {
    return this.commandManager.stateManager.getLocation() === '洞府';
  }

  getUnavailableMessage() {
    return '修炼只能在洞府进行。';
  }

  execute(arg) {
    return Skills.cultivate(this.commandManager.stateManager.getState());
  }
}

// 炼丹指令
class RefinePillCommand extends BaseCommand {
  constructor(commandManager) {
    super('refine_pill', '炼丹', '在洞府中炼制丹药', true, commandManager);
    this.commandManager = commandManager;
  }

  isAvailable() {
    return this.commandManager.stateManager.getLocation() === '洞府';
  }

  getUnavailableMessage() {
    return '炼丹只能在洞府进行。';
  }

  execute(arg) {
    // 直接打开炼丹页面，让用户在页面中选择要炼制的丹药
    return [{ type: 'open_refine_modal', refineType: 'pill', itemName: arg || '' }];
  }
}

// 炼器指令
class RefineTreasureCommand extends BaseCommand {
  constructor(commandManager) {
    super('refine_treasure', '炼器', '在洞府中炼制法宝', true, commandManager);
    this.commandManager = commandManager;
  }

  isAvailable() {
    return this.commandManager.stateManager.getLocation() === '洞府';
  }

  getUnavailableMessage() {
    return '炼器只能在洞府进行。';
  }

  execute(arg) {
    // 直接打开炼器页面，让用户在页面中选择要炼制的法宝
    return [{ type: 'open_refine_modal', refineType: 'treasure', itemName: arg || '' }];
  }
}

// 起名指令
class NameCommand extends BaseCommand {
  constructor(commandManager) {
    super('name', null, '设置角色名字', true, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    if (!arg) return ['请指定名字，例如 name 张三。'];
    if (this.commandManager.stateManager.isNamed()) return ['你已经起过名了，无法重复起名。'];
    this.commandManager.stateManager.setName(arg);
    const state = this.commandManager.stateManager.getState();
    const spiritualRoot = state.spiritualRoot;
    return [
      `你的名字已设置为：${arg}。祝你旅途愉快！`,
      `天赋灵根：${spiritualRoot.name}`,
      `灵根说明：${spiritualRoot.description}`,
      '灵根系统介绍：灵根决定修炼速度和潜力，不同灵根可通过提纯获得特殊能力。'
    ];
  }
}

// 物品信息指令
class InfoCommand extends BaseCommand {
  constructor(commandManager) {
    super('info', null, '查看物品信息', false, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    if (!arg) return ['请指定物品名称，例如 info 灵草。'];
    if (!this.commandManager.stateManager.hasItem(arg)) return [`你的背包中没有 ${arg}。`];
    return [UtilsManager.getItemInfo(arg)];
  }
}

// 发现指令
class DiscoveriesCommand extends BaseCommand {
  constructor(commandManager) {
    super('discoveries', null, '查看探索发现', false, commandManager);
    this.commandManager = commandManager;
  }

  execute(arg) {
    return this.commandManager.explorationManager.showDiscoveries();
  }
}

// 注册所有指令的函数
export function registerAllCommands(commandManager) {
  commandManager.registerCommand(new HelpCommand(commandManager));
  commandManager.registerCommand(new StatusCommand(commandManager));
  commandManager.registerCommand(new ExploreCommand(commandManager));
  commandManager.registerCommand(new LearnCommand(commandManager));
  commandManager.registerCommand(new UseCommand(commandManager));
  commandManager.registerCommand(new JoinCommand(commandManager));
  commandManager.registerCommand(new BagCommand(commandManager));
  commandManager.registerCommand(new TravelCommand(commandManager));
  commandManager.registerCommand(new ResetCommand(commandManager));
  commandManager.registerCommand(new CultivateCommand(commandManager));
  commandManager.registerCommand(new RefinePillCommand(commandManager));
  commandManager.registerCommand(new RefineTreasureCommand(commandManager));
  commandManager.registerCommand(new NameCommand(commandManager));
  commandManager.registerCommand(new InfoCommand(commandManager));
  commandManager.registerCommand(new DiscoveriesCommand(commandManager));
}
